import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import CropIcon from "@mui/icons-material/Crop";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error(`Failed to read ${file?.name || "image"}`));
    reader.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = src;
  });
}

function clampPositiveInt(value, fallback) {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed < 1) return fallback;
  return parsed;
}

function buildCropLabel(imageIndex, cropIndex, rowIndex, colIndex) {
  return `Image ${imageIndex + 1} · Crop ${cropIndex + 1} · R${rowIndex + 1} C${colIndex + 1}`;
}

function buildDetectedCropLabel(imageIndex, cropIndex) {
  return `Image ${imageIndex + 1} · Detected Card ${cropIndex + 1}`;
}

function buildAdjustedCropLabel(imageIndex, cropIndex, rowIndex, colIndex) {
  return `Image ${imageIndex + 1} · Adjusted Crop ${cropIndex + 1} · R${rowIndex + 1} C${colIndex + 1}`;
}

function smoothSeries(values, radius = 4) {
  return values.map((_, index) => {
    let total = 0;
    let count = 0;
    for (let offset = -radius; offset <= radius; offset += 1) {
      const nextIndex = index + offset;
      if (nextIndex >= 0 && nextIndex < values.length) {
        total += values[nextIndex];
        count += 1;
      }
    }
    return count ? total / count : values[index];
  });
}

function getMean(values) {
  if (!values.length) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function getStandardDeviation(values, mean) {
  if (!values.length) return 0;
  const variance = values.reduce((sum, value) => sum + (value - mean) ** 2, 0) / values.length;
  return Math.sqrt(variance);
}

function findBands(values, options = {}) {
  const {
    minBandPixels = 8,
    minGapBetweenBands = 18,
    thresholdBias = 0.8,
  } = options;

  const mean = getMean(values);
  const stdDev = getStandardDeviation(values, mean);
  const threshold = mean + stdDev * thresholdBias;
  const rawBands = [];
  let start = null;

  for (let index = 0; index < values.length; index += 1) {
    if (values[index] >= threshold) {
      if (start === null) start = index;
    } else if (start !== null) {
      const end = index - 1;
      if (end - start + 1 >= minBandPixels) {
        rawBands.push({ start, end, center: Math.round((start + end) / 2) });
      }
      start = null;
    }
  }

  if (start !== null) {
    const end = values.length - 1;
    if (end - start + 1 >= minBandPixels) {
      rawBands.push({ start, end, center: Math.round((start + end) / 2) });
    }
  }

  const merged = [];
  rawBands.forEach((band) => {
    const last = merged[merged.length - 1];
    if (!last) {
      merged.push({ ...band });
      return;
    }
    if (band.start - last.end <= minGapBetweenBands) {
      last.end = band.end;
      last.center = Math.round((last.start + last.end) / 2);
      return;
    }
    merged.push({ ...band });
  });

  return merged;
}

function buildSegments(bands, totalSize) {
  const boundaries = [0, ...bands.map((band) => band.center), totalSize];
  const segments = [];

  for (let index = 0; index < boundaries.length - 1; index += 1) {
    const start = boundaries[index];
    const end = boundaries[index + 1];
    const size = end - start;
    if (size < totalSize * 0.05) continue;
    segments.push({ start, end, size, center: Math.round((start + end) / 2) });
  }

  return segments;
}

function filterCardLikeSegments(segments, totalSize) {
  if (!segments.length) return [];
  const meanSize = getMean(segments.map((segment) => segment.size));
  return segments.filter((segment) => segment.size >= meanSize * 0.45 && segment.size <= totalSize * 0.88);
}

function limitSegments(segments, maxCount = 6) {
  if (segments.length <= maxCount) return segments;
  return [...segments].sort((a, b) => b.size - a.size).slice(0, maxCount).sort((a, b) => a.start - b.start);
}

function normalizeBands(bands, totalSize) {
  return bands.filter((band) => band.center > totalSize * 0.03 && band.center < totalSize * 0.97);
}

function buildSignalSeries(imageData, width, height, mode = "base") {
  const { data } = imageData;
  const vertical = new Array(width).fill(0);
  const horizontal = new Array(height).fill(0);
  const verticalEdges = new Array(width).fill(0);
  const horizontalEdges = new Array(height).fill(0);
  const grayscale = new Array(width * height).fill(0);

  const contrastBoost = mode === "contrast" ? 1.35 : mode === "soft" ? 1.1 : 1;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const offset = (y * width + x) * 4;
      const r = data[offset];
      const g = data[offset + 1];
      const b = data[offset + 2];
      const brightness = (r + g + b) / 3;
      const adjusted = Math.max(0, Math.min(255, 128 + (brightness - 128) * contrastBoost));
      const darkness = 255 - adjusted;
      grayscale[y * width + x] = adjusted;
      vertical[x] += darkness;
      horizontal[y] += darkness;
    }
  }

  for (let y = 1; y < height; y += 1) {
    for (let x = 1; x < width; x += 1) {
      const current = grayscale[y * width + x];
      const left = grayscale[y * width + (x - 1)];
      const up = grayscale[(y - 1) * width + x];
      verticalEdges[x] += Math.abs(current - left);
      horizontalEdges[y] += Math.abs(current - up);
    }
  }

  return {
    verticalDarkness: smoothSeries(vertical.map((value) => value / height), 6),
    horizontalDarkness: smoothSeries(horizontal.map((value) => value / width), 6),
    verticalEdges: smoothSeries(verticalEdges.map((value) => value / Math.max(1, height)), 4),
    horizontalEdges: smoothSeries(horizontalEdges.map((value) => value / Math.max(1, width)), 4),
  };
}

function mergeBoxes(boxes, width, height) {
  const merged = [];
  const overlapThreshold = 0.45;

  function overlapRatio(a, b) {
    const x1 = Math.max(a.x, b.x);
    const y1 = Math.max(a.y, b.y);
    const x2 = Math.min(a.x + a.width, b.x + b.width);
    const y2 = Math.min(a.y + a.height, b.y + b.height);
    const overlapWidth = Math.max(0, x2 - x1);
    const overlapHeight = Math.max(0, y2 - y1);
    const overlapArea = overlapWidth * overlapHeight;
    const smallestArea = Math.min(a.width * a.height, b.width * b.height) || 1;
    return overlapArea / smallestArea;
  }

  boxes
    .sort((a, b) => (b.score || 0) - (a.score || 0))
    .forEach((box) => {
      const existing = merged.find((candidate) => overlapRatio(candidate, box) >= overlapThreshold);
      if (!existing) {
        merged.push({ ...box });
        return;
      }
      if ((box.score || 0) > (existing.score || 0)) {
        existing.x = box.x;
        existing.y = box.y;
        existing.width = box.width;
        existing.height = box.height;
        existing.score = box.score;
        existing.source = box.source;
      }
    });

  return merged
    .map((box, index) => ({
      ...box,
      id: box.id || `detected-${index}`,
      x: Math.max(0, Math.min(width - 1, Math.round(box.x))),
      y: Math.max(0, Math.min(height - 1, Math.round(box.y))),
      width: Math.max(1, Math.min(width - Math.round(box.x), Math.round(box.width))),
      height: Math.max(1, Math.min(height - Math.round(box.y), Math.round(box.height))),
    }))
    .sort((a, b) => {
      if (Math.abs(a.y - b.y) > 30) return a.y - b.y;
      return a.x - b.x;
    });
}

function clusterCenters(values, tolerance) {
  const sorted = [...values].sort((a, b) => a - b);
  const groups = [];
  sorted.forEach((value) => {
    const last = groups[groups.length - 1];
    if (!last || Math.abs(last.center - value) > tolerance) {
      groups.push({ values: [value], center: value });
      return;
    }
    last.values.push(value);
    last.center = getMean(last.values);
  });
  return groups;
}

function inferGridBoxes(boxes, width, height) {
  if (boxes.length < 3) return [];

  const medianWidth = [...boxes].sort((a, b) => a.width - b.width)[Math.floor(boxes.length / 2)]?.width || 0;
  const medianHeight = [...boxes].sort((a, b) => a.height - b.height)[Math.floor(boxes.length / 2)]?.height || 0;
  if (!medianWidth || !medianHeight) return [];

  const xGroups = clusterCenters(boxes.map((box) => box.x + box.width / 2), medianWidth * 0.45).slice(0, 5);
  const yGroups = clusterCenters(boxes.map((box) => box.y + box.height / 2), medianHeight * 0.45).slice(0, 5);

  if (xGroups.length < 2 || yGroups.length < 2) return [];

  const inferred = [];
  yGroups.forEach((row, rowIndex) => {
    xGroups.forEach((col, colIndex) => {
      const existing = boxes.find((box) => {
        const centerX = box.x + box.width / 2;
        const centerY = box.y + box.height / 2;
        return Math.abs(centerX - col.center) <= medianWidth * 0.28 && Math.abs(centerY - row.center) <= medianHeight * 0.28;
      });
      if (existing) {
        existing.rowIndex = rowIndex;
        existing.colIndex = colIndex;
        return;
      }

      const x = Math.round(col.center - medianWidth / 2);
      const y = Math.round(row.center - medianHeight / 2);
      if (x < 0 || y < 0 || x + medianWidth > width || y + medianHeight > height) return;
      inferred.push({
        id: `inferred-${rowIndex}-${colIndex}`,
        x,
        y,
        width: Math.round(medianWidth),
        height: Math.round(medianHeight),
        rowIndex,
        colIndex,
        score: 0.35,
        source: "layout inference",
        inferred: true,
      });
    });
  });

  return inferred;
}

function buildCandidateBoxesFromSegments(columnSegments, rowSegments, width, height, source) {
  const boxes = [];
  rowSegments.forEach((rowSegment, rowIndex) => {
    columnSegments.forEach((columnSegment, colIndex) => {
      const paddingX = Math.max(2, Math.round(columnSegment.size * 0.018));
      const paddingY = Math.max(2, Math.round(rowSegment.size * 0.018));
      const x = Math.max(0, columnSegment.start + paddingX);
      const y = Math.max(0, rowSegment.start + paddingY);
      const x2 = Math.min(width, columnSegment.end - paddingX);
      const y2 = Math.min(height, rowSegment.end - paddingY);
      const boxWidth = Math.max(1, x2 - x);
      const boxHeight = Math.max(1, y2 - y);
      const aspectRatio = boxWidth / boxHeight;
      if (boxWidth < width * 0.08 || boxHeight < height * 0.1) return;
      if (aspectRatio < 0.42 || aspectRatio > 0.86) return;
      const aspectScore = 1 - Math.min(1, Math.abs(aspectRatio - 0.715) / 0.25);
      const areaScore = Math.min(1, (boxWidth * boxHeight) / (width * height * 0.12));
      boxes.push({
        x,
        y,
        width: boxWidth,
        height: boxHeight,
        rowIndex,
        colIndex,
        score: aspectScore * 0.7 + areaScore * 0.3,
        source,
      });
    });
  });
  return boxes;
}

function buildObjectBoxesFromImageData(imageData, width, height) {
  const passes = ["base", "contrast", "soft"];
  let candidateBoxes = [];

  passes.forEach((mode) => {
    const signals = buildSignalSeries(imageData, width, height, mode);
    const bandConfigs = [
      { source: `${mode} darkness`, x: signals.verticalDarkness, y: signals.horizontalDarkness, bias: 0.72 },
      { source: `${mode} edges`, x: signals.verticalEdges, y: signals.horizontalEdges, bias: 0.55 },
    ];

    bandConfigs.forEach((config) => {
      const verticalBands = normalizeBands(
        findBands(config.x, {
          minBandPixels: Math.max(5, Math.round(width * 0.008)),
          minGapBetweenBands: Math.max(8, Math.round(width * 0.012)),
          thresholdBias: config.bias,
        }),
        width
      );
      const horizontalBands = normalizeBands(
        findBands(config.y, {
          minBandPixels: Math.max(5, Math.round(height * 0.008)),
          minGapBetweenBands: Math.max(8, Math.round(height * 0.012)),
          thresholdBias: config.bias,
        }),
        height
      );

      const columnSegments = limitSegments(filterCardLikeSegments(buildSegments(verticalBands, width), width), 6);
      const rowSegments = limitSegments(filterCardLikeSegments(buildSegments(horizontalBands, height), height), 6);

      if (columnSegments.length >= 1 && rowSegments.length >= 1) {
        candidateBoxes = candidateBoxes.concat(buildCandidateBoxesFromSegments(columnSegments, rowSegments, width, height, config.source));
      }
    });
  });

  let boxes = mergeBoxes(candidateBoxes, width, height);
  const inferred = inferGridBoxes(boxes, width, height);
  boxes = mergeBoxes(boxes.concat(inferred), width, height).map((box, index) => ({
    ...box,
    id: box.id || `detected-${index}`,
  }));

  return boxes;
}

function rotateImageToCanvas(image, angle) {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (angle === 90) {
    canvas.width = image.height;
    canvas.height = image.width;
    ctx.translate(canvas.width, 0);
    ctx.rotate(Math.PI / 2);
  } else {
    canvas.width = image.width;
    canvas.height = image.height;
  }
  ctx.drawImage(image, 0, 0);
  return canvas;
}

function mapBoxesFromRotation(boxes, originalWidth, originalHeight, angle) {
  if (angle !== 90) return boxes;
  return boxes.map((box, index) => ({
    ...box,
    id: box.id || `rotated-${index}`,
    x: box.y,
    y: Math.max(0, originalHeight - (box.x + box.width)),
    width: box.height,
    height: box.width,
    rotationApplied: angle,
  }));
}

async function detectBestBoxesForImage(image) {
  const loadedImage = await loadImage(image.sourceDataUrl);
  const variants = [
    { angle: 0, canvas: rotateImageToCanvas(loadedImage, 0) },
  ];
  if (loadedImage.width > loadedImage.height * 1.1) {
    variants.push({ angle: 90, canvas: rotateImageToCanvas(loadedImage, 90) });
  }

  let best = { boxes: [], angle: 0, score: -1 };

  variants.forEach((variant) => {
    const ctx = variant.canvas.getContext("2d");
    const imageData = ctx.getImageData(0, 0, variant.canvas.width, variant.canvas.height);
    const candidateBoxes = buildObjectBoxesFromImageData(imageData, variant.canvas.width, variant.canvas.height);
    const mappedBoxes = mapBoxesFromRotation(candidateBoxes, loadedImage.width, loadedImage.height, variant.angle);
    const score = mappedBoxes.reduce((sum, box) => sum + (box.score || 0), 0) + mappedBoxes.length * 0.4;
    if (score > best.score) {
      best = { boxes: mappedBoxes, angle: variant.angle, score };
    }
  });

  return {
    loadedImage,
    angleUsed: best.angle,
    boxes: best.boxes,
  };
}

async function cropBoxesFromImage({ image, boxes, imageIndex }) {
  const loadedImage = await loadImage(image.sourceDataUrl);
  const crops = [];

  for (let index = 0; index < boxes.length; index += 1) {
    const box = boxes[index];
    const canvas = document.createElement("canvas");
    canvas.width = box.width;
    canvas.height = box.height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(
      loadedImage,
      box.x,
      box.y,
      box.width,
      box.height,
      0,
      0,
      box.width,
      box.height
    );

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.95));
    if (!blob) {
      throw new Error(`Failed to create detected crop for ${image.name}`);
    }

    crops.push({
      id: `${image.id}-detected-crop-${index}`,
      label: buildDetectedCropLabel(imageIndex, index),
      previewUrl: URL.createObjectURL(blob),
      width: box.width,
      height: box.height,
      rowIndex: box.rowIndex,
      colIndex: box.colIndex,
      x: box.x,
      y: box.y,
      inferred: !!box.inferred,
      detectionMethod: box.inferred ? "layout-assisted inference" : (box.source || "multi-pass heuristic"),
    });
  }

  return crops;
}


function createDefaultGridConfig(width, height, rows, cols) {
  const xBoundaries = [];
  const yBoundaries = [];
  for (let index = 0; index <= cols; index += 1) {
    xBoundaries.push(index / cols);
  }
  for (let index = 0; index <= rows; index += 1) {
    yBoundaries.push(index / rows);
  }
  return { width, height, rows, cols, xBoundaries, yBoundaries, cellAdjustments: {} };
}

function cloneGridConfig(gridConfig) {
  return {
    ...gridConfig,
    xBoundaries: [...(gridConfig?.xBoundaries || [])],
    yBoundaries: [...(gridConfig?.yBoundaries || [])],
    cellAdjustments: { ...(gridConfig?.cellAdjustments || {}) },
  };
}

function getGridCellKey(rowIndex, colIndex) {
  return `${rowIndex}-${colIndex}`;
}

function getGridBoxesFromConfig(gridConfig, width, height) {
  if (!gridConfig) return [];
  const boxes = [];
  const xBoundaries = gridConfig.xBoundaries || [];
  const yBoundaries = gridConfig.yBoundaries || [];
  for (let rowIndex = 0; rowIndex < yBoundaries.length - 1; rowIndex += 1) {
    for (let colIndex = 0; colIndex < xBoundaries.length - 1; colIndex += 1) {
      const x = Math.round((xBoundaries[colIndex] || 0) * width);
      const y = Math.round((yBoundaries[rowIndex] || 0) * height);
      const nextX = Math.round((xBoundaries[colIndex + 1] || 1) * width);
      const nextY = Math.round((yBoundaries[rowIndex + 1] || 1) * height);
      const adjustment = gridConfig.cellAdjustments?.[getGridCellKey(rowIndex, colIndex)] || {};
      const minCellSize = 24;
      const adjustedX = Math.max(0, Math.min(width - minCellSize, x + (adjustment.dx || 0)));
      const adjustedY = Math.max(0, Math.min(height - minCellSize, y + (adjustment.dy || 0)));
      const adjustedWidth = Math.max(minCellSize, Math.min(width - adjustedX, Math.max(1, nextX - x) + (adjustment.dw || 0)));
      const adjustedHeight = Math.max(minCellSize, Math.min(height - adjustedY, Math.max(1, nextY - y) + (adjustment.dh || 0)));
      boxes.push({
        id: `grid-${rowIndex}-${colIndex}`,
        x: adjustedX,
        y: adjustedY,
        width: adjustedWidth,
        height: adjustedHeight,
        rowIndex,
        colIndex,
        source: 'manual grid',
      });
    }
  }
  return boxes;
}

async function createGridCropsForImage({ image, gridConfig, imageIndex }) {
  const loadedImage = await loadImage(image.sourceDataUrl);
  const boxes = getGridBoxesFromConfig(gridConfig, loadedImage.width, loadedImage.height);
  const crops = [];

  for (let index = 0; index < boxes.length; index += 1) {
    const box = boxes[index];
    const canvas = document.createElement('canvas');
    canvas.width = box.width;
    canvas.height = box.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(loadedImage, box.x, box.y, box.width, box.height, 0, 0, box.width, box.height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.95));
    if (!blob) {
      throw new Error(`Failed to create adjusted crop for ${image.name}`);
    }
    const baseName = (image.name || `image_${imageIndex + 1}`).replace(/\.[^.]+$/, '');
    const file = new File([blob], `${baseName}_crop_r${box.rowIndex + 1}_c${box.colIndex + 1}.jpg`, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
    crops.push({
      id: `${image.id}-adjusted-crop-${box.rowIndex}-${box.colIndex}`,
      label: buildAdjustedCropLabel(imageIndex, index, box.rowIndex, box.colIndex),
      previewUrl: URL.createObjectURL(blob),
      width: box.width,
      height: box.height,
      rowIndex: box.rowIndex,
      colIndex: box.colIndex,
      x: box.x,
      y: box.y,
      detectionMethod: 'manual grid adjustment',
      file,
    });
  }

  return { boxes, crops, width: loadedImage.width, height: loadedImage.height };
}

function DraggableGridOverlay({ image, gridConfig, onStartDrag, onSelectCell, selectedCellKey = null }) {
  if (!image || !gridConfig) return null;
  const xBoundaries = gridConfig.xBoundaries || [];
  const yBoundaries = gridConfig.yBoundaries || [];
  return (
    <Box sx={{ position: 'absolute', inset: 0 }}>
      {getGridBoxesFromConfig(gridConfig, image.width, image.height).map((box) => {
        const cellKey = getGridCellKey(box.rowIndex, box.colIndex);
        const isSelected = selectedCellKey === cellKey;
        return (
          <Box
            key={box.id}
            onClick={() => onSelectCell?.({ rowIndex: box.rowIndex, colIndex: box.colIndex })}
            sx={{
              position: 'absolute',
              left: `${(box.x / image.width) * 100}%`,
              top: `${(box.y / image.height) * 100}%`,
              width: `${(box.width / image.width) * 100}%`,
              height: `${(box.height / image.height) * 100}%`,
              border: '2px solid',
              borderColor: isSelected ? 'success.main' : 'warning.main',
              boxSizing: 'border-box',
              bgcolor: isSelected ? 'rgba(46, 125, 50, 0.12)' : 'rgba(255, 167, 38, 0.06)',
              cursor: 'pointer',
              pointerEvents: 'auto',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                transform: 'translate(0, -100%)',
                bgcolor: isSelected ? 'success.main' : 'warning.main',
                color: 'common.white',
                px: 0.75,
                py: 0.25,
                fontSize: 11,
                fontWeight: 700,
                borderTopLeftRadius: 4,
                borderTopRightRadius: 4,
              }}
            >
              {box.rowIndex + 1},{box.colIndex + 1}
            </Box>
          </Box>
        );
      })}
      {xBoundaries.slice(1, -1).map((boundary, index) => (
        <React.Fragment key={`x-${index}`}>
          <Box
            sx={{
              position: 'absolute',
              left: `${boundary * 100}%`,
              top: 0,
              bottom: 0,
              width: 2,
              bgcolor: 'warning.main',
              transform: 'translateX(-1px)',
            }}
          />
          <Box
            onMouseDown={(event) => onStartDrag(event, 'x', index + 1)}
            onTouchStart={(event) => onStartDrag(event, 'x', index + 1)}
            sx={{
              position: 'absolute',
              left: `${boundary * 100}%`,
              top: '50%',
              width: 18,
              height: 48,
              transform: 'translate(-50%, -50%)',
              bgcolor: 'warning.main',
              borderRadius: 1,
              border: '2px solid white',
              boxSizing: 'border-box',
              cursor: 'col-resize',
              pointerEvents: 'auto',
            }}
          />
        </React.Fragment>
      ))}
      {yBoundaries.slice(1, -1).map((boundary, index) => (
        <React.Fragment key={`y-${index}`}>
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: `${boundary * 100}%`,
              height: 2,
              bgcolor: 'warning.main',
              transform: 'translateY(-1px)',
            }}
          />
          <Box
            onMouseDown={(event) => onStartDrag(event, 'y', index + 1)}
            onTouchStart={(event) => onStartDrag(event, 'y', index + 1)}
            sx={{
              position: 'absolute',
              left: '50%',
              top: `${boundary * 100}%`,
              width: 48,
              height: 18,
              transform: 'translate(-50%, -50%)',
              bgcolor: 'warning.main',
              borderRadius: 1,
              border: '2px solid white',
              boxSizing: 'border-box',
              cursor: 'row-resize',
              pointerEvents: 'auto',
            }}
          />
        </React.Fragment>
      ))}
    </Box>
  );
}

function SourcePreviewWithBoxes({ image, showGridOverlay = false, gridConfig = null, onStartDrag = null, onSelectCell = null, selectedCellKey = null }) {
  if (!image) {
    return (
      <Typography variant="body2" color="text.secondary">
        Select an image to preview it.
      </Typography>
    );
  }

  const boxes = image.detectionBoxes || [];

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {image.name}
      </Typography>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxHeight: 520,
          borderRadius: 1,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.default",
          overflow: "hidden",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Box
          component="img"
          src={image.sourcePreviewDataUrl || image.sourceDataUrl}
          alt={image.name}
          sx={{ width: "100%", maxHeight: 520, objectFit: "contain", display: "block" }}
        />
        {showGridOverlay && gridConfig ? (
          <DraggableGridOverlay image={image} gridConfig={gridConfig} onStartDrag={onStartDrag} onSelectCell={onSelectCell} selectedCellKey={selectedCellKey} />
        ) : null}
        {boxes.length && !showGridOverlay ? (
          <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
            {boxes.map((box, index) => (
              <Box
                key={box.id || `${box.x}-${box.y}-${index}`}
                sx={{
                  position: "absolute",
                  left: `${(box.x / image.width) * 100}%`,
                  top: `${(box.y / image.height) * 100}%`,
                  width: `${(box.width / image.width) * 100}%`,
                  height: `${(box.height / image.height) * 100}%`,
                  border: "2px solid",
                  borderColor: "primary.main",
                  boxSizing: "border-box",
                  bgcolor: "rgba(25, 118, 210, 0.08)",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    transform: "translate(0, -100%)",
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    px: 0.75,
                    py: 0.25,
                    fontSize: 11,
                    fontWeight: 700,
                    borderTopLeftRadius: 4,
                    borderTopRightRadius: 4,
                  }}
                >
                  {index + 1}
                </Box>
              </Box>
            ))}
          </Box>
        ) : null}
      </Box>
      {boxes.length ? (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          Detected {boxes.length} card-like regions.
        </Typography>
      ) : null}
    </Box>
  );
}

export default function ImageCropDialog({ open, onClose, onUseCrops }) {
  const [rows, setRows] = useState(3);
  const [cols, setCols] = useState(3);
  const [images, setImages] = useState([]);
  const [selectedImageId, setSelectedImageId] = useState(null);
  const [selectedCropId, setSelectedCropId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lastMode, setLastMode] = useState("grid");
  const [dragState, setDragState] = useState(null);
  const [selectedGridCell, setSelectedGridCell] = useState(null);
  const [adjustStep, setAdjustStep] = useState(10);
  const previewWrapperRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setError("");
      setLoading(false);
    }
  }, [open]);

  useEffect(() => {
    return () => {
      images.forEach((image) => {
        (image.crops || []).forEach((crop) => {
          if (crop?.previewUrl) {
            URL.revokeObjectURL(crop.previewUrl);
          }
        });
      });
    };
  }, [images]);

  const selectedImage = useMemo(
    () => images.find((image) => image.id === selectedImageId) || images[0] || null,
    [images, selectedImageId]
  );

  const selectedCrop = useMemo(() => {
    if (!selectedImage) return null;
    return selectedImage.crops?.find((crop) => crop.id === selectedCropId) || selectedImage.crops?.[0] || null;
  }, [selectedImage, selectedCropId]);

  const selectedGridCellKey = useMemo(() => {
    if (selectedGridCell) return getGridCellKey(selectedGridCell.rowIndex, selectedGridCell.colIndex);
    if (selectedCrop && Number.isInteger(selectedCrop.rowIndex) && Number.isInteger(selectedCrop.colIndex)) {
      return getGridCellKey(selectedCrop.rowIndex, selectedCrop.colIndex);
    }
    return null;
  }, [selectedGridCell, selectedCrop]);

  const updateImageGridConfig = useCallback((imageId, updater) => {
    setImages((prev) => prev.map((image) => {
      if (image.id !== imageId || !image.gridConfig) return image;
      const nextGridConfig = cloneGridConfig(typeof updater === 'function' ? updater(image.gridConfig) : updater);
      return { ...image, gridConfig: nextGridConfig };
    }));
  }, []);

  const handleStartGridDrag = useCallback((event, axis, boundaryIndex) => {
    if (!selectedImage?.gridConfig) return;
    event.preventDefault();
    event.stopPropagation();
    setDragState({ imageId: selectedImage.id, axis, boundaryIndex });
  }, [selectedImage]);

  useEffect(() => {
    if (!dragState || !previewWrapperRef.current || !selectedImage?.gridConfig || dragState.imageId !== selectedImage.id) return undefined;

    const minGap = 0.06;

    const handlePointerMove = (event) => {
      const touch = event.touches?.[0];
      const clientX = touch ? touch.clientX : event.clientX;
      const clientY = touch ? touch.clientY : event.clientY;
      const rect = previewWrapperRef.current.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      updateImageGridConfig(selectedImage.id, (currentGridConfig) => {
        const nextGridConfig = cloneGridConfig(currentGridConfig);
        if (dragState.axis === 'x') {
          const nextValue = Math.min(1 - minGap, Math.max(minGap, (clientX - rect.left) / rect.width));
          const minValue = (nextGridConfig.xBoundaries[dragState.boundaryIndex - 1] || 0) + minGap;
          const maxValue = (nextGridConfig.xBoundaries[dragState.boundaryIndex + 1] || 1) - minGap;
          nextGridConfig.xBoundaries[dragState.boundaryIndex] = Math.min(maxValue, Math.max(minValue, nextValue));
        } else {
          const nextValue = Math.min(1 - minGap, Math.max(minGap, (clientY - rect.top) / rect.height));
          const minValue = (nextGridConfig.yBoundaries[dragState.boundaryIndex - 1] || 0) + minGap;
          const maxValue = (nextGridConfig.yBoundaries[dragState.boundaryIndex + 1] || 1) - minGap;
          nextGridConfig.yBoundaries[dragState.boundaryIndex] = Math.min(maxValue, Math.max(minValue, nextValue));
        }
        return nextGridConfig;
      });
    };

    const handlePointerUp = () => setDragState(null);

    window.addEventListener('mousemove', handlePointerMove);
    window.addEventListener('mouseup', handlePointerUp);
    window.addEventListener('touchmove', handlePointerMove, { passive: false });
    window.addEventListener('touchend', handlePointerUp);

    return () => {
      window.removeEventListener('mousemove', handlePointerMove);
      window.removeEventListener('mouseup', handlePointerUp);
      window.removeEventListener('touchmove', handlePointerMove);
      window.removeEventListener('touchend', handlePointerUp);
    };
  }, [dragState, selectedImage, updateImageGridConfig]);

  const handleSelectGridCell = useCallback((cell) => {
    setSelectedGridCell(cell);
    if (!selectedImage) return;
    const matchingCrop = selectedImage.crops?.find((crop) => crop.rowIndex === cell.rowIndex && crop.colIndex === cell.colIndex);
    if (matchingCrop?.id) {
      setSelectedCropId(matchingCrop.id);
    }
  }, [selectedImage]);

  const handleAdjustSelectedCell = useCallback((change) => {
    if (!selectedImage?.gridConfig || !selectedGridCellKey) return;
    const step = clampPositiveInt(adjustStep, 10);
    updateImageGridConfig(selectedImage.id, (currentGridConfig) => {
      const nextGridConfig = cloneGridConfig(currentGridConfig);
      const currentAdjustment = nextGridConfig.cellAdjustments?.[selectedGridCellKey] || { dx: 0, dy: 0, dw: 0, dh: 0 };
      nextGridConfig.cellAdjustments[selectedGridCellKey] = {
        dx: currentAdjustment.dx + (change.dx || 0) * step,
        dy: currentAdjustment.dy + (change.dy || 0) * step,
        dw: currentAdjustment.dw + (change.dw || 0) * step,
        dh: currentAdjustment.dh + (change.dh || 0) * step,
      };
      return nextGridConfig;
    });
  }, [adjustStep, selectedGridCellKey, selectedImage, updateImageGridConfig]);

  const handleResetSelectedCell = useCallback(() => {
    if (!selectedImage?.gridConfig || !selectedGridCellKey) return;
    updateImageGridConfig(selectedImage.id, (currentGridConfig) => {
      const nextGridConfig = cloneGridConfig(currentGridConfig);
      delete nextGridConfig.cellAdjustments[selectedGridCellKey];
      return nextGridConfig;
    });
  }, [selectedGridCellKey, selectedImage, updateImageGridConfig]);

  const handleFilesPicked = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setLoading(true);
    setError("");
    try {
      const prepared = await Promise.all(
        files.map(async (file, index) => {
          const sourceDataUrl = await readFileAsDataUrl(file);
          const loadedImage = await loadImage(sourceDataUrl);
          return {
            id: `${Date.now()}-${index}-${file.name}`,
            name: file.name || `image-${index + 1}.jpg`,
            file,
            sourceDataUrl,
            sourcePreviewDataUrl: sourceDataUrl,
            width: loadedImage.width,
            height: loadedImage.height,
            crops: [],
            detectionBoxes: [],
          };
        })
      );
      setImages((prev) => [...prev, ...prepared]);
      setSelectedImageId((prev) => prev || prepared[0]?.id || null);
    } catch (err) {
      setError(err?.message || "Failed to load selected images.");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCrops = async () => {
    if (!images.length) {
      setError("Select at least one image first.");
      return;
    }

    const safeRows = clampPositiveInt(rows, 3);
    const safeCols = clampPositiveInt(cols, 3);
    setRows(safeRows);
    setCols(safeCols);
    setLoading(true);
    setError("");

    try {
      const nextImages = await Promise.all(
        images.map(async (image, imageIndex) => {
          (image.crops || []).forEach((crop) => {
            if (crop?.previewUrl) {
              URL.revokeObjectURL(crop.previewUrl);
            }
          });

          const gridConfig = createDefaultGridConfig(image.width, image.height, safeRows, safeCols);
          const generated = await createGridCropsForImage({ image, gridConfig, imageIndex });

          return {
            ...image,
            gridConfig,
            sourcePreviewDataUrl: image.sourceDataUrl,
            detectionBoxes: generated.boxes,
            crops: generated.crops,
            detectionMeta: {
              detectedCount: generated.crops.length,
              inferredCount: 0,
              backend: 'manual grid',
            },
            detectionError: '',
          };
        })
      );

      setImages(nextImages);
      setLastMode('grid');
      const firstImage = nextImages[0] || null;
      setSelectedImageId((prev) => prev || firstImage?.id || null);
      const matchingImage = nextImages.find((image) => image.id === (selectedImageId || firstImage?.id));
      setSelectedCropId(matchingImage?.crops?.[0]?.id || firstImage?.crops?.[0]?.id || null);
    } catch (err) {
      setError(err?.message || 'Failed to generate crops.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshAdjustedGrid = async () => {
    if (!images.some((image) => image.gridConfig)) {
      setError('Generate grid crops first.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const nextImages = await Promise.all(images.map(async (image, imageIndex) => {
        if (!image.gridConfig) return image;
        (image.crops || []).forEach((crop) => {
          if (crop?.previewUrl?.startsWith?.('blob:')) {
            URL.revokeObjectURL(crop.previewUrl);
          }
        });
        const generated = await createGridCropsForImage({ image, gridConfig: image.gridConfig, imageIndex });
        return {
          ...image,
          detectionBoxes: generated.boxes,
          crops: generated.crops,
          detectionMeta: { detectedCount: generated.crops.length, inferredCount: 0, backend: 'manual grid adjustment' },
          detectionError: '',
        };
      }));
      setImages(nextImages);
      const activeImage = nextImages.find((image) => image.id === selectedImageId) || nextImages[0] || null;
      setSelectedCropId(activeImage?.crops?.[0]?.id || null);
      setLastMode('grid');
    } catch (err) {
      setError(err?.message || 'Failed to refresh adjusted grid crops.');
    } finally {
      setLoading(false);
    }
  };

  const handleUseAdjustedGridForSuggest = async () => {
    if (!onUseCrops) {
      setError('Crop handoff is not available in this screen.');
      return;
    }
    if (!images.some((image) => image.gridConfig)) {
      setError('Generate grid crops first.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const croppedFiles = [];
      for (let imageIndex = 0; imageIndex < images.length; imageIndex += 1) {
        const image = images[imageIndex];
        if (!image.gridConfig) continue;
        const generated = await createGridCropsForImage({ image, gridConfig: image.gridConfig, imageIndex });
        generated.crops.forEach((crop) => croppedFiles.push(crop.file));
        generated.crops.forEach((crop) => {
          if (crop?.previewUrl?.startsWith?.('blob:')) {
            URL.revokeObjectURL(crop.previewUrl);
          }
        });
      }
      if (!croppedFiles.length) {
        throw new Error('No adjusted crops were created.');
      }
      await onUseCrops(croppedFiles);
      onClose?.();
    } catch (err) {
      setError(err?.message || 'Failed to send adjusted crops to Suggest From Image.');
    } finally {
      setLoading(false);
    }
  };

  const handleDetectCards = async () => {
    if (!images.length) {
      setError("Select at least one image first.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const formData = new FormData();
      images.forEach((image) => {
        if (image.file) {
          formData.append("images", image.file, image.name);
        }
      });

      const response = await axios.post("/api/cards/lookup/detect-crops", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (!response?.data?.ok) {
        throw new Error(response?.data?.error || "Backend crop detection failed.");
      }

      const results = response.data.data || [];
      const nextImages = images.map((image, imageIndex) => {
        (image.crops || []).forEach((crop) => {
          if (crop?.previewUrl?.startsWith?.("blob:")) {
            URL.revokeObjectURL(crop.previewUrl);
          }
        });

        const result = results[imageIndex] || {};
        const nextCrops = (result.crops || []).map((crop, cropIndex) => ({
          id: `${image.id}-backend-crop-${cropIndex}`,
          label: crop.label || buildDetectedCropLabel(imageIndex, cropIndex),
          previewUrl: crop.preview_data_uri,
          width: crop.width,
          height: crop.height,
          inferred: !!crop.inferred,
          detectionMethod: crop.detection_method || "opencv",
        }));

        return {
          ...image,
          sourcePreviewDataUrl: result.source_preview_data_uri || image.sourceDataUrl,
          width: result.source_width || image.width,
          height: result.source_height || image.height,
          gridConfig: null,
          detectionBoxes: result.boxes || [],
          crops: nextCrops,
          detectionMeta: result.detection_meta || { angleUsed: 0, detectedCount: nextCrops.length, inferredCount: 0 },
          detectionError: result.error || "",
        };
      });

      setImages(nextImages);
      setLastMode("detect");
      const firstDetected = nextImages.find((image) => image.crops?.length) || nextImages[0] || null;
      setSelectedImageId(firstDetected?.id || null);
      setSelectedCropId(firstDetected?.crops?.[0]?.id || null);

      const detectedCount = nextImages.reduce((sum, image) => sum + (image.crops?.length || 0), 0);
      if (!detectedCount) {
        setError("No card-like regions were detected by the backend crop service. Try a clearer image or fall back to grid crops.");
      }
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Failed to detect cards.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        Crop Image
        <IconButton onClick={handleClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info">
            Use <strong>Generate Grid Crops</strong> for the main editable workflow. You can drag the orange grid divider handles in Source Preview, refresh the adjusted crop set, then send the final crops into <strong>Suggest From Image</strong>. Backend Detect Cards remains available as an alternate OpenCV-based test path.
          </Alert>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "stretch", md: "center" }} flexWrap="wrap">
            <Button variant="outlined" startIcon={<UploadIcon />} component="label" disabled={loading}>
              Select Images
              <input
                hidden
                accept="image/*"
                type="file"
                multiple
                onChange={(e) => {
                  handleFilesPicked(e.target.files);
                  e.target.value = "";
                }}
              />
            </Button>
            <TextField
              label="Rows"
              type="number"
              value={rows}
              onChange={(e) => setRows(e.target.value)}
              inputProps={{ min: 1, max: 10 }}
              sx={{ width: 120 }}
            />
            <TextField
              label="Columns"
              type="number"
              value={cols}
              onChange={(e) => setCols(e.target.value)}
              inputProps={{ min: 1, max: 10 }}
              sx={{ width: 120 }}
            />
            <Button variant="contained" startIcon={<CropIcon />} onClick={handleGenerateCrops} disabled={loading || !images.length}>
              {loading && lastMode === "grid" ? "Processing..." : "Generate Grid Crops"}
            </Button>
            <Button
              variant="outlined"
              startIcon={<AutoAwesomeIcon />}
              onClick={handleDetectCards}
              disabled={loading || !images.length}
            >
              {loading && lastMode === "detect" ? "Detecting..." : "Detect Cards"}
            </Button>
            <Button variant="outlined" onClick={handleRefreshAdjustedGrid} disabled={loading || !images.some((image) => image.gridConfig)}>
              Refresh Adjusted Grid
            </Button>
            <Button variant="contained" color="success" onClick={handleUseAdjustedGridForSuggest} disabled={loading || !images.some((image) => image.gridConfig)}>
              Use Grid Crops for Suggest From Image
            </Button>
            <Typography variant="body2" color="text.secondary">
              Loaded images: {images.length}
            </Typography>
          </Stack>

          {error ? <Alert severity="error">{error}</Alert> : null}

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "320px 1fr 420px" }, gap: 2, alignItems: "start" }}>
            <Paper variant="outlined" sx={{ p: 2, maxHeight: 560, overflow: "auto" }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Images
              </Typography>
              <Stack spacing={1}>
                {images.length ? (
                  images.map((image) => {
                    const isActive = image.id === selectedImage?.id;
                    return (
                      <Button
                        key={image.id}
                        variant={isActive ? "contained" : "outlined"}
                        onClick={() => {
                          setSelectedImageId(image.id);
                          setSelectedCropId(image.crops?.[0]?.id || null);
                          setSelectedGridCell(image.crops?.[0] ? { rowIndex: image.crops[0].rowIndex, colIndex: image.crops[0].colIndex } : null);
                        }}
                        sx={{ justifyContent: "space-between" }}
                      >
                        <span>{image.name}</span>
                        <span>{image.crops?.length || 0}</span>
                      </Button>
                    );
                  })
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No images selected yet.
                  </Typography>
                )}
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Source Preview
              </Typography>
              <Box ref={previewWrapperRef}>
                <SourcePreviewWithBoxes image={selectedImage} showGridOverlay={lastMode === "grid" && !!selectedImage?.gridConfig} gridConfig={selectedImage?.gridConfig || null} onStartDrag={handleStartGridDrag} onSelectCell={handleSelectGridCell} selectedCellKey={selectedGridCellKey} />
              </Box>
              {selectedImage?.gridConfig && lastMode === "grid" ? (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                    Drag the orange divider handles to adjust the crop lines, click a crop cell to fine-tune it, then press Refresh Adjusted Grid or Use Grid Crops for Suggest From Image.
                  </Typography>
                  <Paper variant="outlined" sx={{ mt: 1.5, p: 1.5, bgcolor: 'background.default' }}>
                    <Stack spacing={1.25}>
                      <Typography variant="subtitle2">
                        Fine Adjust Selected Crop {selectedGridCellKey ? `(${selectedGridCellKey.replace('-', ', ')})` : ''}
                      </Typography>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
                        <TextField
                          label="Adjust step (px)"
                          type="number"
                          size="small"
                          value={adjustStep}
                          onChange={(event) => setAdjustStep(event.target.value)}
                          inputProps={{ min: 1, max: 100 }}
                          sx={{ width: 150 }}
                        />
                        <Button size="small" variant="outlined" startIcon={<RestartAltIcon />} onClick={handleResetSelectedCell} disabled={!selectedGridCellKey}>
                          Reset Selected Crop
                        </Button>
                      </Stack>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button size="small" variant="outlined" onClick={() => handleAdjustSelectedCell({ dx: -1 })} disabled={!selectedGridCellKey}>Move Left</Button>
                        <Button size="small" variant="outlined" onClick={() => handleAdjustSelectedCell({ dx: 1 })} disabled={!selectedGridCellKey}>Move Right</Button>
                        <Button size="small" variant="outlined" onClick={() => handleAdjustSelectedCell({ dy: -1 })} disabled={!selectedGridCellKey}>Move Up</Button>
                        <Button size="small" variant="outlined" onClick={() => handleAdjustSelectedCell({ dy: 1 })} disabled={!selectedGridCellKey}>Move Down</Button>
                        <Button size="small" variant="outlined" onClick={() => handleAdjustSelectedCell({ dw: -1 })} disabled={!selectedGridCellKey}>Width -</Button>
                        <Button size="small" variant="outlined" onClick={() => handleAdjustSelectedCell({ dw: 1 })} disabled={!selectedGridCellKey}>Width +</Button>
                        <Button size="small" variant="outlined" onClick={() => handleAdjustSelectedCell({ dh: -1 })} disabled={!selectedGridCellKey}>Height -</Button>
                        <Button size="small" variant="outlined" onClick={() => handleAdjustSelectedCell({ dh: 1 })} disabled={!selectedGridCellKey}>Height +</Button>
                      </Stack>
                    </Stack>
                  </Paper>
                </>
              ) : null}
              {selectedImage?.detectionMeta ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  Detection summary: {selectedImage.detectionMeta.detected_count || selectedImage.detectionMeta.detectedCount || 0} detected, {selectedImage.detectionMeta.inferred_count || selectedImage.detectionMeta.inferredCount || 0} inferred{(selectedImage.detectionMeta.angle_used || selectedImage.detectionMeta.angleUsed) ? `, rotated ${selectedImage.detectionMeta.angle_used || selectedImage.detectionMeta.angleUsed}° for detection` : ""}{selectedImage.detectionMeta.backend ? `, backend ${selectedImage.detectionMeta.backend}` : ""}.
                </Typography>
              ) : null}
              {selectedImage?.detectionError ? (
                <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
                  {selectedImage.detectionError}
                </Typography>
              ) : null}
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, maxHeight: 560, overflow: "auto" }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Crop Viewer
              </Typography>
              {selectedImage?.crops?.length ? (
                <Stack spacing={1.5}>
                  {selectedImage.crops.map((crop) => {
                    const isActive = crop.id === selectedCrop?.id;
                    return (
                      <Paper
                        key={crop.id}
                        variant="outlined"
                        onClick={() => {
                          setSelectedCropId(crop.id);
                          if (Number.isInteger(crop.rowIndex) && Number.isInteger(crop.colIndex)) {
                            setSelectedGridCell({ rowIndex: crop.rowIndex, colIndex: crop.colIndex });
                          }
                        }}
                        sx={{ p: 1, cursor: "pointer", borderColor: isActive ? "primary.main" : "divider" }}
                      >
                        <Stack direction="row" spacing={1.5}>
                          <Box
                            component="img"
                            src={crop.previewUrl}
                            alt={crop.label}
                            sx={{ width: 88, height: 120, objectFit: "contain", borderRadius: 1, border: "1px solid", borderColor: "divider", bgcolor: "background.default" }}
                          />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {crop.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {crop.width} × {crop.height}px
                            </Typography>
                            <Tooltip title={crop.previewUrl}>
                              <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                {crop.detectionMethod ? `${crop.inferred ? "Inferred using" : "Detected using"} ${crop.detectionMethod}` : "Ready for manual review"}
                              </Typography>
                            </Tooltip>
                          </Box>
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Generate crops or run backend Detect Cards to review the output here.
                </Typography>
              )}
            </Paper>
          </Box>

          {selectedCrop ? (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Selected Crop Preview
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {selectedCrop.label}
              </Typography>
              <Box
                component="img"
                src={selectedCrop.previewUrl}
                alt={selectedCrop.label}
                sx={{ width: "100%", maxHeight: 420, objectFit: "contain", borderRadius: 1, border: "1px solid", borderColor: "divider", bgcolor: "background.default" }}
              />
            </Paper>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
